import { useState } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { AgentProxyService } from '@/services/agentProxyService'
import { useToast } from '@/hooks/useToast'

interface CreateEventViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onEventCreated: () => void
}

interface EventCreate {
  name: string
  definition: string
  eventType: 'ONE TIME' | 'RECURRING'
  intervalValue?: number
  intervalField?: 'YEAR' | 'MONTH' | 'WEEK' | 'DAY' | 'HOUR' | 'MINUTE' | 'SECOND' | 'QUARTER'
  starts?: string
  ends?: string
  executeAt?: string
  status?: 'ENABLE' | 'DISABLE'
  onCompletion?: 'PRESERVE' | 'NOT PRESERVE'
  comment?: string
  enabled?: boolean
}

export function CreateEventView({ connection, database, onEventCreated }: CreateEventViewProps) {
  const [formData, setFormData] = useState<EventCreate>({
    name: '',
    definition: '',
    eventType: 'RECURRING',
    intervalValue: 1,
    intervalField: 'HOUR',
    onCompletion: 'NOT PRESERVE',
    enabled: true,
    comment: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showToast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim() || !formData.definition.trim()) {
      showToast('error', 'Please fill in all required fields')
      return
    }

    if (formData.eventType === 'ONE TIME' && !formData.executeAt) {
      showToast('error', 'Please specify execution time for one-time events')
      return
    }

    if (formData.eventType === 'RECURRING' && (!formData.intervalValue || formData.intervalValue <= 0)) {
      showToast('error', 'Please specify a valid interval for recurring events')
      return
    }

    try {
      setIsSubmitting(true)
      
      // Create event using agent-proxy
      await AgentProxyService.createEvent(
        connection.id,
        database,
        {
          event_name: formData.name.trim(),
          event_type: formData.eventType,
          interval_value: formData.intervalValue,
          interval_field: formData.intervalField,
          starts: formData.starts,
          ends: formData.ends,
          execute_at: formData.executeAt,
          status: formData.enabled ? 'ENABLE' : 'DISABLE',
          on_completion: formData.onCompletion,
          comment: formData.comment?.trim(),
          definition: formData.definition.trim()
        }
      )
      
      showToast('success', 'Event created successfully')
      onEventCreated()
    } catch (error) {
      console.error('Failed to create event:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create event'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    onEventCreated()
  }

  const formatDateTimeLocal = (date: Date) => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Create New Event
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Create a scheduled event for automated execution in MySQL database {database}
          </p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Event Configuration
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Event Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Event Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="my_event"
                  required
                />
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Event Type *
                </label>
                <select
                  value={formData.eventType}
                  onChange={(e) => { setFormData({ ...formData, eventType: e.target.value === 'ONE TIME' ? 'ONE TIME' : 'RECURRING' }) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="RECURRING">Recurring</option>
                  <option value="ONE TIME">One Time</option>
                </select>
              </div>

              {/* Enabled */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={formData.enabled}
                    onChange={(e) => { setFormData({ ...formData, enabled: e.target.checked }) }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="enabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Enable event immediately
                  </label>
                </div>
              </div>

              {/* On Completion */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  On Completion
                </label>
                <select
                  value={formData.onCompletion}
                  onChange={(e) => { setFormData({ ...formData, onCompletion: e.target.value === 'PRESERVE' ? 'PRESERVE' : 'NOT PRESERVE' }) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="NOT PRESERVE">Not Preserve</option>
                  <option value="PRESERVE">Preserve</option>
                </select>
              </div>
            </div>
          </div>

          {/* Timing Configuration */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Timing Configuration
            </h2>
            
            {formData.eventType === 'ONE TIME' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Execute At *
                </label>
                <input
                  type="datetime-local"
                  value={formData.executeAt || formatDateTimeLocal(new Date(Date.now() + 60000))}
                  onChange={(e) => { setFormData({ ...formData, executeAt: e.target.value }) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
            )}

            {formData.eventType === 'RECURRING' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Interval Value *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.intervalValue || ''}
                    onChange={(e) => { setFormData({ ...formData, intervalValue: Number.parseInt(e.target.value) || 1 }) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Interval Field *
                  </label>
                  <select
                    value={formData.intervalField}
                    onChange={(e) => {
                      const allowed = ['SECOND','MINUTE','HOUR','DAY','WEEK','MONTH','QUARTER','YEAR'] as const
                      const v = e.target.value
                      const isAllowed = (val: string): val is NonNullable<EventCreate['intervalField']> => (allowed as readonly string[]).includes(val)
                      const next = isAllowed(v) ? v : 'HOUR'
                      setFormData({ ...formData, intervalField: next })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="SECOND">Second</option>
                    <option value="MINUTE">Minute</option>
                    <option value="HOUR">Hour</option>
                    <option value="DAY">Day</option>
                    <option value="WEEK">Week</option>
                    <option value="MONTH">Month</option>
                    <option value="QUARTER">Quarter</option>
                    <option value="YEAR">Year</option>
                    <option value="MICROSECOND">Microsecond</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Starts
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.starts || ''}
                    onChange={(e) => { setFormData({ ...formData, starts: e.target.value }) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ends (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.ends || ''}
                    onChange={(e) => { setFormData({ ...formData, ends: e.target.value }) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Event Definition */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Event Definition
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SQL Statement *
              </label>
              <textarea
                value={formData.definition}
                onChange={(e) => { setFormData({ ...formData, definition: e.target.value }) }}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                placeholder="-- SQL statements to execute&#10;INSERT INTO log_table (message, created_at) VALUES ('Event executed', NOW());"
                required
              />
            </div>

            {/* Comment */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comment (Optional)
              </label>
              <input
                type="text"
                value={formData.comment || ''}
                onChange={(e) => { setFormData({ ...formData, comment: e.target.value }) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Optional description of the event"
              />
            </div>
          </div>

          {/* Example */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
              Example Event
            </h3>
            <div className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
              <p><strong>Cleanup Event:</strong> Delete old log entries every day</p>
              <div className="bg-blue-100 dark:bg-blue-900/40 rounded-lg p-3">
                <pre className="font-mono whitespace-pre-wrap">
{`DELETE FROM application_logs 
WHERE created_at < DATE_SUB(NOW(), INTERVAL 30 DAY);`}
                </pre>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Event...
                </>
              ) : (
                'Create Event'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
